'use client'

import { Plus } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import React, { useState } from 'react'

import { cn } from '@/utilities/ui'

type Props = {
  itens: { q: string; a: string }[]
  /** `linha`: lista com divisórias e «+»; `icone`: ícone circular azul à esquerda, item aberto em cartão cinza. */
  estilo?: 'linha' | 'icone'
}

function IconeAlvo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  )
}

/** Acordeão de perguntas (um aberto por vez; o primeiro aberto por padrão). */
export function AcordeaoFaq({ itens, estilo = 'linha' }: Props) {
  const [aberto, setAberto] = useState<number | null>(0)

  if (estilo === 'icone') {
    return (
      <div className="flex flex-col gap-2">
        {itens.map((it, i) => {
          const ativo = aberto === i
          return (
            <div key={i} className={cn('ag-card transition-colors duration-300', ativo ? 'bg-[var(--ag-surface-light)]' : 'bg-transparent')}>
              <h3>
                <button
                  type="button"
                  aria-expanded={ativo}
                  onClick={() => setAberto(ativo ? null : i)}
                  className="flex w-full cursor-pointer items-center gap-5 px-5 py-5 text-left lg:px-7 lg:py-6"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--ag-primary)] text-[var(--ag-text-light)]">
                    <IconeAlvo />
                  </span>
                  <span className="ag-heading flex-1 text-[18px] lg:text-[22px]">{it.q}</span>
                  <Plus className={cn('h-5 w-5 shrink-0 transition-transform duration-300', ativo && 'rotate-45')} strokeWidth={1.5} />
                </button>
              </h3>
              <AnimatePresence initial={false}>
                {ativo ? (
                  <motion.div
                    key="a"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <p className="whitespace-pre-line px-5 pb-6 text-[15px] leading-[1.6] text-[var(--sec-muted)] lg:px-7 lg:pl-[88px] lg:text-[16px]">{it.a}</p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {itens.map((it, i) => {
        const ativo = aberto === i
        return (
          <div key={i} className="border-b-[0.5px] border-[rgb(var(--sec-border-rgb)/0.2)]">
            <h3>
              <button
                type="button"
                aria-expanded={ativo}
                onClick={() => setAberto(ativo ? null : i)}
                className="flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left lg:py-6"
              >
                <span className="ag-heading text-[18px] lg:text-[22px]">{it.q}</span>
                <Plus className={cn('h-5 w-5 shrink-0 transition-transform duration-300', ativo && 'rotate-45')} strokeWidth={1.5} />
              </button>
            </h3>
            <AnimatePresence initial={false}>
              {ativo ? (
                <motion.div
                  key="a"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="overflow-hidden"
                >
                  <p className="max-w-[720px] whitespace-pre-line pb-6 text-[15px] leading-[1.6] text-[var(--sec-muted)] lg:text-[16px]">{it.a}</p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
